import React from 'react';
import PropTypes from 'prop-types';
import { ArrowRight } from 'lucide-react';
import Badge from '../../Badge';
import Hyperlink from '../../Hyperlink';
import './ContentBannerV4.css';

/**
 * ContentBannerV4 — a row of white info cards on a gray-025 band. Each card:
 * a product photo in a fixed-ratio box with a promo chip over its top-right
 * corner («GRATIS», «INGÅR ALLTID»), a title, P.Regular copy and a text link
 * with an arrow (Crown Student homepage: Black Box / Storleksguide / Trygghet
 * och garantier).
 *
 * Figma (Crownstudent-MCP `HoZ60grGAGMTrlmGnOJxwV`): desktop `69:19626`
 * (1600×578: three 459px cards, 48 / 24 gaps, card padding 32, the photo box
 * 812:440). No mobile frame — the cards stack one per row (user decision
 * 2026-10-28).
 *
 * - `items` — one entry per card: `title` (styled `typo-h3`; the element is
 *   `titleAs`, h2 by default), `text` (P.Regular, gray-6), `image`
 *   `{ src, alt }` shown `object-fit: cover` in the box, `chip` (the DS Badge
 *   `brand` / `l` / `square` over the photo — none without text or photo),
 *   `linkLabel` with `linkHref` / `linkOnClick` (DS Hyperlink regular + arrow).
 *   A link WITHOUT `linkHref` has no target yet: it renders `href="#"` and the
 *   click's default is prevented (the page never jumps), `linkOnClick` still
 *   runs. A card without an image has no photo box; one without a label has no
 *   link.
 * - Renders nothing without items. Every value is a DS token — no component
 *   tokens. CSS hooks a host may set: `--content-banner-v4-ratio` (812 / 440),
 *   `--content-banner-v4-columns` (3 from 1024px).
 */
const ContentBannerV4 = ({
  items = [],
  titleAs: Title = 'h2',
  className = '',
  ...rest
}) => {
  const cards = (items || []).filter(Boolean);
  if (cards.length === 0) return null;

  return (
    <section className={['content-banner-v4', className].filter(Boolean).join(' ')} {...rest}>
      <div className="content-banner-v4__container">
        <div className="content-banner-v4__grid">
          {cards.map((item, index) => {
            const { title, text, image, chip, linkLabel, linkHref, linkOnClick } = item;
            const handleLink = (event) => {
              if (!linkHref) event.preventDefault();
              linkOnClick?.(event);
            };
            return (
              <article key={title || image?.src || index} className="content-banner-v4__item">
                {image?.src && (
                  <figure className="content-banner-v4__media">
                    <img
                      className="content-banner-v4__img"
                      src={image.src}
                      alt={image.alt || ''}
                      loading="lazy"
                    />
                    {chip && (
                      <Badge variant="brand" size="l" shape="square" className="content-banner-v4__chip">
                        {chip}
                      </Badge>
                    )}
                  </figure>
                )}

                <div className="content-banner-v4__copy">
                  {title && <Title className="content-banner-v4__title typo-h3">{title}</Title>}
                  {text && <p className="content-banner-v4__text typo-body">{text}</p>}
                  {linkLabel && (
                    <Hyperlink
                      variant="regular"
                      href={linkHref || '#'}
                      onClick={handleLink}
                      iconRight={<ArrowRight size={20} />}
                      showIconRight
                      className="content-banner-v4__link"
                    >
                      {linkLabel}
                    </Hyperlink>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

ContentBannerV4.propTypes = {
  /** One card each: { title, text, image: { src, alt }, chip, linkLabel, linkHref, linkOnClick } */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      text: PropTypes.string,
      image: PropTypes.shape({
        src: PropTypes.string.isRequired,
        alt: PropTypes.string,
      }),
      chip: PropTypes.string,
      linkLabel: PropTypes.string,
      linkHref: PropTypes.string,
      linkOnClick: PropTypes.func,
    }),
  ),
  /** Heading element of every card title — h2 by default; h3 under a section's own h2 (the style is always typo-h3) */
  titleAs: PropTypes.elementType,
  /** Additional CSS class on the section (other attributes reach it too) */
  className: PropTypes.string,
};

export default ContentBannerV4;
